import "../../assets/css/views/subRetailManagement/posBulkUpload.css";
import { useTranslation } from "react-i18next";
import {
  Button,
  FilePicker,
  Group,
  IconInfo,
  IconWarningAlt,
  Modal,
  Table,
  TableColumns,
  TableData,
} from "@veneer/core";
import { useMemo } from "react";
import Loader from "../../components/Loader/loader";
import LoaderModal from "../../components/Loader/loaderModal";
import { usePosBulkUpload } from "../../hooks/pos/usePosBulkUpload";
import SimpleMessageModalWithOpenClose from "../../components/Modals/SimpleMessageModalWithOpenClose";

export default function PosBulkUpload() {
  const [t] = useTranslation("global");
  const {
    handleCreatePos,
    loading,
    handleDownloadTemplate,
    loadingModal,
    openModalCreate,
    handleOnClickExit,
    handleShowPreview,
    fileName,
    errorFilePicker,
    errorMessage,
    handleFileChange,
    submitPos,
    onClearFilePicker,
    openModalCreateDone,
    openModalError,
    setOpenModalCreateDone,
    setOpenModalError,
    phase,
    selectedFile,
    parsedHeaders,
    validatedRows,
    confirmDisabled,
    bulkResult,
    resetResultState,
    handleDownloadErrorReport,
  } = usePosBulkUpload();

  const isPreviewPhase = phase === "preview";
  const isUploadingPhase = phase === "uploading";
  const isResultPhase = phase === "result";

  const previewColumns: TableColumns[] = useMemo(
    () => parsedHeaders.map((header) => ({ id: header.id, label: header.label })),
    [parsedHeaders]
  );

  const previewData: TableData[] = useMemo(() => validatedRows, [validatedRows]);

  const errorColumns: TableColumns[] = useMemo(
    () => [
      { id: "rowNumber", label: t("header.rowNumber") },
      { id: "description", label: t("header.errorDescription") },
    ],
    [t]
  );

  const errorData: TableData[] = useMemo(() => bulkResult?.errors ?? [], [bulkResult]);

  // One-shot outcome message shown right after upload, before the user reviews the results table.
  const uploadSummaryMessage = useMemo(() => {
    if (!bulkResult) return "";
    if (bulkResult.totalRejected === 0) return t("modal.bulkUploadCompleteSuccess");
    if (bulkResult.totalCreated === 0) return t("modal.bulkUploadCompleteFailure");
    return t("modal.bulkUploadPartialSuccess", {
      successCount: bulkResult.totalCreated,
      failedCount: bulkResult.totalRejected,
    });
  }, [bulkResult, t]);

  const handleCloseResults = () => {
    resetResultState();
  };

  return (
    <div className="pos-bulk-upload-container">
      {loading && <Loader />}
      <h4 className="pos-bulk-upload-title">{t("header.posBulkUploadTitle")}</h4>

      {!isPreviewPhase && !isUploadingPhase && !isResultPhase && (
        <div className="pos-bulk-upload-buttons">
          <div>
            <Button onClick={handleCreatePos} style={{ margin: "5px" }}>
              {t("header.uploadFile")}
            </Button>
          </div>

          <div className="pos-bulk-upload-buttons-right">
            <Button onClick={handleDownloadTemplate} style={{ margin: "5px" }}>
              {t("header.downloadTemplate")}
            </Button>
          </div>
        </div>
      )}

      {loadingModal && <LoaderModal />}

      <div style={{ position: "relative" }}>
        <Modal
          align="center"
          closeButton
          id="modal-Create"
          show={openModalCreate}
          onClose={handleOnClickExit}
          footer={
            <div className="buttonGroup">
              <Group>
                <Button appearance="secondary" onClick={handleOnClickExit}>
                  {t("header.closebutton")}
                </Button>
                <Button onClick={handleShowPreview} disabled={phase !== "preview"}>
                  {t("header.previewButton")}
                </Button>
              </Group>
            </div>
          }
          title={t("header.posBulkUploadTitle")}
        >
          <div>
            <div className="pos-bulk-upload-max-limit">
              <IconInfo />
              <span>{t("modal.posBulkUploadMaxLimit")}</span>
            </div>
            <FilePicker
              className="pos-bulk-upload-file-picker"
              id="pos-bulk-upload-file-picker"
              label={t("header.uploadFile")}
              accept=".xlsx"
              onChange={handleFileChange}
              onClear={onClearFilePicker}
              value={fileName}
              error={errorFilePicker}
              helperText={errorMessage}
            />
          </div>
        </Modal>
      </div>

      {(isPreviewPhase || isUploadingPhase) && (
        <div className="pos-bulk-upload-preview-section">
          <h5>{t("header.filePreview")}</h5>
          <p className="pos-bulk-upload-summary">
            {selectedFile?.name} — {t("header.showing")} {validatedRows.length} {t("header.records")}
          </p>
          {isPreviewPhase && validatedRows.length > 0 && (
            <div className="pos-bulk-upload-table">
              <Table columns={previewColumns} data={previewData} />
            </div>
          )}
          {isPreviewPhase && validatedRows.length === 0 && (
            <div className="alert-container">
              <div>
                <IconWarningAlt style={{ width: "80px", height: "80px" }} />
              </div>
              <p>{t("header.noItemsFound")}</p>
            </div>
          )}
          {isPreviewPhase && (
            <div className="pos-bulk-upload-actions">
              <Group>
                <Button appearance="secondary" onClick={onClearFilePicker}>
                  {t("header.cancel")}
                </Button>
                <Button onClick={submitPos} disabled={confirmDisabled}>
                  {t("header.sendButton")}
                </Button>
              </Group>
            </div>
          )}
        </div>
      )}

      {isResultPhase && bulkResult && (
        <div className="pos-bulk-upload-preview-section">
          <h5>{t("header.posBulkUploadTitle")}</h5>

          <div className="pos-bulk-upload-stats">
            <div className="pos-bulk-upload-stat-card">
              <div className="pos-bulk-upload-stat-value">{bulkResult.totalProcessed}</div>
              <div className="pos-bulk-upload-stat-label">{t("header.totalProcessed")}</div>
            </div>
            <div className="pos-bulk-upload-stat-card">
              <div className="pos-bulk-upload-stat-value">{bulkResult.totalCreated}</div>
              <div className="pos-bulk-upload-stat-label">{t("header.totalCreated")}</div>
            </div>
            <div className="pos-bulk-upload-stat-card">
              <div className="pos-bulk-upload-stat-value">{bulkResult.totalRejected}</div>
              <div className="pos-bulk-upload-stat-label">{t("header.totalRejected")}</div>
            </div>
          </div>

          <p className={errorData.length === 0 ? "pos-bulk-upload-summary pos-bulk-upload-summary-centered" : "pos-bulk-upload-summary"} aria-live="polite">
            {uploadSummaryMessage}
          </p>

          {errorData.length > 0 && (
            <div className="pos-bulk-upload-table">
              <Table columns={errorColumns} data={errorData} />
            </div>
          )}

          <div className="pos-bulk-upload-actions pos-bulk-upload-actions-end">
            <Group>
              {errorData.length > 0 && (
                <Button
                  className="pos-bulk-upload-download-errors-button"
                  appearance="secondary"
                  onClick={() => handleDownloadErrorReport(bulkResult.errors)}
                >
                  {t("header.downloadErrorReport")}
                </Button>
              )}
              <Button onClick={handleCloseResults}>{t("header.closebutton")}</Button>
            </Group>
          </div>
        </div>
      )}

      <SimpleMessageModalWithOpenClose
        showModal={openModalCreateDone}
        setOpenCloseModal={setOpenModalCreateDone}
        modalTitle={t("modal.message")}
        modalMessage={uploadSummaryMessage}
      />

      <SimpleMessageModalWithOpenClose
        showModal={openModalError}
        setOpenCloseModal={setOpenModalError}
        modalTitle={t("header.error")}
        modalMessage={t("modal.generalError")}
      />
    </div>
  );
}
